/* eslint-disable no-underscore-dangle */
import routeNames from '@modules/router/src/routeNames';
import history from '@modules/router/src/history';
import axios, { ResponseType, AxiosResponse } from 'axios';
import { Cookies } from 'react-cookie';
import {
  API_URL, CLIENT_ID, CLIENT_SECRET, REFRESH_TOKEN_TYPE,
} from './constants';

export enum ContentType {
  JSON = 'JSON',
  FORM_DATA = 'FORM_DATA',
}

export enum AcceptType {
  JSON = 'JSON',
  PDF = 'PDF',
}

const getToken = () => {
  const cookies = new Cookies();
  const tokenType = cookies.get('tokenType');
  const accessToken = cookies.get('accessToken');
  return `${tokenType} ${accessToken}`;
};

function getHeader(auth = true, acceptType = AcceptType.JSON, contentType = ContentType.JSON) {
  const header = {};

  if (auth) {
    const token = getToken();
    Object.assign(header, { Authorization: token });
  } else {
    Object.assign(header, { 'Access-Control-Allow-Origin': '*' });
  }

  switch (acceptType) {
    case AcceptType.PDF:
      Object.assign(header, { Accept: 'application/pdf' });
      break;
    default:
      Object.assign(header, { Accept: 'application/json' });
      break;
  }

  switch (contentType) {
    case ContentType.FORM_DATA:
      Object.assign(header, { 'Content-Type': 'multipart/form-data' });
      break;
    default:
      Object.assign(header, { 'Content-Type': 'application/json charset=UTF-8' });
      break;
  }

  return header;
}

interface ApiConfig {
  auth?: boolean;
  acceptType: AcceptType;
  contentType: ContentType;
  responseType: ResponseType;
}

export async function getRequest<ReturnType extends unknown>(
  url: string,
  params?: any,
  config: ApiConfig = {
    acceptType: AcceptType.JSON,
    contentType: ContentType.JSON,
    responseType: 'json',
  },
): Promise<AxiosResponse<ReturnType>> {
  const headers = getHeader(config.auth, config.acceptType, config.contentType);

  return axios({
    method: 'get',
    url: API_URL + url,
    headers,
    params,
    responseType: config.responseType,
  });
}

export async function postRequest<ReturnType extends unknown>(
  url: string,
  data?: any,
  config: ApiConfig = {
    acceptType: AcceptType.JSON,
    contentType: ContentType.JSON,
    responseType: 'json',
  },
): Promise<AxiosResponse<ReturnType>> {
  const headers = getHeader(config.auth, config.acceptType, config.contentType);

  const options = {
    headers,
    responseType: config.responseType,
  };

  return axios.post(API_URL + url, data, options);
}

// for multiple requests
let isRefreshing = false;
let failedQueue = [] as any[];

const processQueue = (error: any, token: AuthToken | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

axios.interceptors.response.use((response) => response, (error) => {
  const originalRequest = error.config;
  const cookies = new Cookies();

  if (error.response.status === 401 && originalRequest.url.includes('refresh-token')) {
    cookies.remove('accessToken');
    cookies.remove('tokenType');
    cookies.remove('refreshToken');
    history.push(routeNames.home);
    return Promise.reject(error);
  }

  if (error.response.status === 401 && !originalRequest._retry && !originalRequest.url.includes('login')) {
    const refreshToken = cookies.get('refreshToken');

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token: any) => {
        originalRequest.headers.Authorization = `${token.tokenType} ${token.tokenApi}`;
        return axios(originalRequest);
      }).catch((err) => {
        return Promise.reject(err);
      });
    }

    isRefreshing = true;
    originalRequest._retry = true;

    const refreshTokenPayload = {
      grant_type: REFRESH_TOKEN_TYPE,
      client_secret: CLIENT_SECRET,
      client_id: CLIENT_ID,
      refresh_token: refreshToken,
    };

    return postRequest<Auth.RefreshTokenResponse>('/oauth2/refresh-token', refreshTokenPayload, {
      acceptType: AcceptType.JSON,
      contentType: ContentType.JSON,
      responseType: 'json',
    }).then((tokenRefreshResponse) => {
      // 1) put token to cookie
      cookies.set('refreshToken', tokenRefreshResponse.data.refresh_token, {
        path: '/',
      });
      cookies.set('tokenType', tokenRefreshResponse.data.token_type, {
        path: '/',
      });
      cookies.set('accessToken', tokenRefreshResponse.data.access_token, {
        path: '/',
      });
      // 2) Change Authorization header
      axios.defaults.headers.common.Authorization = `${tokenRefreshResponse.data.token_type} ${tokenRefreshResponse.data.access_token}`;
      processQueue(null,
        {
          tokenApi: tokenRefreshResponse.data.access_token,
          tokenType: tokenRefreshResponse.data.token_type,
        });
      originalRequest.headers.Authorization = `${tokenRefreshResponse.data.token_type} ${tokenRefreshResponse.data.access_token}`;
      // 3) return originalRequest object with Axios.
      return axios(originalRequest);
    })
      .catch((err: any) => { processQueue(err, null); })
      .finally(() => { isRefreshing = false; });
  }
  return Promise.reject(error);
});
